const DB_NAME = 'iflow-disclosure-keys'
const STORE_NAME = 'keypairs'
const DB_VERSION = 1

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'orgId' })
      }
    }
    req.onsuccess = (e) => resolve((e.target as IDBOpenDBRequest).result)
    req.onerror = () => reject(req.error)
  })
}

export async function isEd25519Supported(): Promise<boolean> {
  try {
    if (typeof crypto === 'undefined' || !crypto.subtle) return false
    await crypto.subtle.generateKey({ name: 'Ed25519' } as EcKeyGenParams, false, ['sign'])
    return true
  } catch {
    return false
  }
}

export async function generateKeypair(): Promise<CryptoKeyPair> {
  return crypto.subtle.generateKey({ name: 'Ed25519' } as EcKeyGenParams, false, ['sign'])
}

export async function storeKeypair(keypair: CryptoKeyPair, orgId: string): Promise<void> {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    const req = store.put({ orgId, keypair })
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

export async function loadKeypair(orgId: string): Promise<CryptoKeyPair | null> {
  try {
    const db = await openDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const store = tx.objectStore(STORE_NAME)
      const req = store.get(orgId)
      req.onsuccess = (e) => {
        const record = (e.target as IDBRequest).result
        resolve(record?.keypair ?? null)
      }
      req.onerror = () => reject(req.error)
    })
  } catch {
    return null
  }
}

export async function exportPublicKeyHex(publicKey: CryptoKey): Promise<string> {
  const buf = await crypto.subtle.exportKey('raw', publicKey)
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

export async function signPayload(privateKey: CryptoKey, payloadBytesHex: string): Promise<string> {
  const bytes = new Uint8Array(
    payloadBytesHex.match(/.{2}/g)!.map((h) => parseInt(h, 16))
  )
  const sig = await crypto.subtle.sign('Ed25519', privateKey, bytes)
  return Array.from(new Uint8Array(sig))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Human-readable fingerprint for a registered Ed25519 public key — a real
 * SHA-256 digest of the actual key bytes (the same convention SSH/PGP use for
 * key fingerprints), formatted as grouped hex for display on the Account
 * Security screen. Not a stored/served value — computed on demand from the
 * real public_key_hex returned by the disclosure keypair API.
 */
export async function computeFingerprint(publicKeyHex: string): Promise<string> {
  const bytes = new Uint8Array(
    (publicKeyHex.match(/.{2}/g) || []).map((h) => parseInt(h, 16))
  )
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  const hex = Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
  const groups = hex.slice(0, 24).match(/.{4}/g) || []
  return `SHA256:${groups.slice(0, 4).join(' ')} · ${groups.slice(4).join(' ')}`
}
